import { setLocale as setParalocale } from '@pricedrop/i18n';
import {
  DEFAULT_LOCALE,
  getDirection,
  localeToLanguageTag,
  type SupportedLocale,
} from '@pricedrop/i18n/locales';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { authClient } from '@/lib/auth-client';
import { PermissionsProvider } from '@/lib/permissions-context';
import { getProfile, type UserPermissions } from '@/lib/profile-api';
import { setSentryUser } from '@/lib/sentry';
import { SessionProvider } from '@/lib/session-context';

export const Route = createFileRoute('/_authed')({
  ssr: false,
  beforeLoad: async ({ location }) => {
    const { data: session, error } = await authClient.getSession();

    if (error || !session) {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.pathname,
        },
      });
    }

    let effectiveLocale: SupportedLocale = DEFAULT_LOCALE;
    let permissions: UserPermissions = {
      canManageItems: false,
      canCreateReviews: false,
      canDeleteReviews: false,
      canModerateReviews: false,
      canCreateNotifications: false,
      canManageNotifications: false,
      canViewUsers: false,
      canManageUsers: false,
      canViewSettings: false,
      canEditSettings: false,
    };
    try {
      const profile = await getProfile();
      effectiveLocale = profile.effectiveLocale;
      permissions = profile.permissions;
    } catch {
      effectiveLocale = DEFAULT_LOCALE;
    }

    const langTag = localeToLanguageTag(effectiveLocale);
    setParalocale(langTag, { reload: false });

    if (typeof document !== 'undefined') {
      document.documentElement.lang = effectiveLocale;
      document.documentElement.dir = getDirection(effectiveLocale);
    }

    return {
      session: session.session,
      user: session.user,
      effectiveLocale,
      permissions,
    };
  },
  component: AuthedLayout,
});

function AuthedLayout() {
  const { session, user, permissions } = Route.useRouteContext();

  setSentryUser(user ? { id: user.id } : null);

  return (
    <SessionProvider session={session} user={user}>
      <PermissionsProvider permissions={permissions}>
        <Outlet />
      </PermissionsProvider>
    </SessionProvider>
  );
}
